import { FC } from 'react';

import classNames from 'classnames';

interface Props {
   src: string;
   alt: string;
   title: string;
   caption?: string;
   height?: string;
   width?: string;
   objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';
   borderRadius?: 'none' | 'smallest' | 'inset' | 'control' | 'largest' | 'rounded';
   zoomed?: boolean;
   overlay?: 'none' | 'default' | 'primary' | 'alternate' | 'theme' | 'gradient-fade-out-black' | 'gradient-fade-out-white' | 'gradient-primary';
   defer?: boolean;
}

export const Image: FC<Props> = ({ src, alt, title, caption, height = 'unset', width = 'unset', objectFit = 'cover', borderRadius = 'null', zoomed = false, overlay = 'none', defer = false }) => {
   const hasOverlay = overlay !== 'none' || caption;

   return (
      <div
         data-testid="image-container"
         className={classNames(
            'relative inline-flex overflow-hidden',
            borderRadius === 'none' && 'rounded-brand-border-radius-squared',
            borderRadius === 'smallest' && 'rounded-brand-border-radius-smallest',
            borderRadius === 'inset' && 'rounded-brand-border-radius-inset',
            borderRadius === 'control' && 'rounded-brand-border-radius-control',
            borderRadius === 'largest' && 'rounded-brand-border-radius-largest',
            borderRadius === 'rounded' && 'rounded-brand-border-radius-rounded',
            hasOverlay && 'relative z-0'
         )}
         style={{ width, height }}
      >
         {overlay !== 'none' && (
            <div
               data-testid="image-overlay"
               className={classNames(
                  'absolute w-full h-full left-0 top-0 opacity-80',
                  overlay === 'default' && 'bg-component-surfaces-overlay-container-color-background-scrimmed-default',
                  overlay === 'primary' && 'bg-component-surfaces-overlay-container-color-background-scrimmed-primary',
                  overlay === 'alternate' && 'bg-component-surfaces-overlay-container-color-background-scrimmed-alternate',
                  overlay === 'theme' && 'bg-component-surfaces-overlay-container-color-background-scrimmed-theme',
                  overlay === 'gradient-fade-out-black' && 'bg-gradient-to-t from-zinc-900 to-transparent',
                  overlay === 'gradient-fade-out-white' && 'bg-component-surfaces-overlay-container-color-background-gradient-fade-out',
                  overlay === 'gradient-primary' && 'bg-component-surfaces-overlay-container-color-background-gradient-primary',
                  borderRadius === 'none' && 'rounded-brand-border-radius-squared',
                  borderRadius === 'smallest' && 'rounded-brand-border-radius-smallest',
                  borderRadius === 'inset' && 'rounded-brand-border-radius-inset',
                  borderRadius === 'control' && 'rounded-brand-border-radius-control',
                  borderRadius === 'largest' && 'rounded-brand-border-radius-largest',
                  borderRadius === 'rounded' && 'rounded-brand-border-radius-rounded'
               )}
            />
         )}

         <img data-testid={`image${zoomed ? '--zoomed' : ''}`} src={src} alt={alt} title={title} style={{ objectFit }} className={`size-full object-cover transition-all duration-500 ${zoomed ? 'scale-110' : ''} ${hasOverlay ? 'z-[-1]' : ''}`} loading={defer ? 'lazy' : undefined} />

         {caption && (
            <div
               data-testid="image-caption"
               className="typography-component-typography-body-regular-sm text-component-data-display-image-caption-container-color-text-on-scrimmed bg-component-data-display-image-caption-container-color-background-scrimmed h-component-data-display-image-caption-sizing-height px-component-data-display-image-caption-container-spacing-padding-x absolute inset-x-0 bottom-0 z-10 flex items-center justify-center"
            >
               {caption}
            </div>
         )}
      </div>
   );
};
